import { useEffect, useState, useRef, useCallback } from "react";
import api, { apiError, fileUrl, formatBytes, formatDuration } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  UploadCloud, Film, Image as ImageIcon, Trash2, RotateCw, CheckCircle2, AlertCircle, Loader2,
  ArrowRight, Play, FolderUp, FileArchive, Sparkles, Wand2, Star, ScanSearch,
} from "lucide-react";
import { toast } from "sonner";

const IMG = ["jpg", "jpeg", "png", "webp", "heic", "heif"];
const VID = ["mp4", "mov", "webm", "m4v"];
const MAX_IMG = 30 * 1024 * 1024;
const MAX_VID = 800 * 1024 * 1024;
const PAGE = 60;

const FILTERS = [
  ["all", "All"],
  ["recommended", "Recommended"],
  ["not_recommended", "Not recommended"],
  ["favourites", "Favourites"],
];

function QualityBadges({ a }) {
  const q = a.quality;
  const tags = [];
  if (q?.is_duplicate) tags.push(["Duplicate", "bg-fuchsia-600/80"]);
  if (q?.is_blurred) tags.push(["Blurry", "bg-orange-600/80"]);
  if (q?.is_dark) tags.push(["Dark", "bg-slate-600/80"]);
  if (!tags.length) return null;
  return (
    <div className="absolute top-1.5 left-1.5 flex flex-col gap-1 items-start">
      {tags.map(([t, c]) => (
        <span key={t} className={`text-[9px] font-semibold text-white px-1.5 py-0.5 rounded ${c}`}>{t}</span>
      ))}
    </div>
  );
}

export default function UploadPanel({ event, reload, goNext }) {
  const [assets, setAssets] = useState([]);
  const [uploads, setUploads] = useState([]);
  const [drag, setDrag] = useState(false);
  const [filter, setFilter] = useState("all");
  const [visible, setVisible] = useState(PAGE);
  const [stats, setStats] = useState(null);
  const [analysis, setAnalysis] = useState(null);
  const [analysing, setAnalysing] = useState(false);
  const [zipBusy, setZipBusy] = useState(false);
  const [bestBusy, setBestBusy] = useState(false);
  const inputRef = useRef();
  const folderRef = useRef();
  const zipRef = useRef();

  const loadAssets = useCallback(async () => {
    try {
      const [{ data }, s] = await Promise.all([
        api.get(`/events/${event.id}/media`, { params: { filter } }),
        api.get(`/events/${event.id}/media/stats`),
      ]);
      setAssets(data);
      setStats(s.data);
    } catch (e) { toast.error(apiError(e)); }
  }, [event.id, filter]);

  useEffect(() => { setVisible(PAGE); loadAssets(); }, [loadAssets]);

  // resume/poll analysis status
  const pollAnalysis = useCallback(async () => {
    try {
      const { data } = await api.get(`/events/${event.id}/analysis`);
      setAnalysis(data);
      return data;
    } catch { return null; }
  }, [event.id]);

  useEffect(() => { pollAnalysis(); }, [pollAnalysis]);

  useEffect(() => {
    if (!analysis || !["queued", "running"].includes(analysis.status)) return;
    const t = setInterval(async () => {
      const d = await pollAnalysis();
      if (d && !["queued", "running"].includes(d.status)) { clearInterval(t); loadAssets(); toast.success("Gallery analysis complete"); }
    }, 2000);
    return () => clearInterval(t);
  }, [analysis, pollAnalysis, loadAssets]);

  const validate = (file) => {
    const ext = file.name.split(".").pop().toLowerCase();
    if (![...IMG, ...VID].includes(ext)) return "Unsupported file type";
    if (IMG.includes(ext) && file.size > MAX_IMG) return "Image exceeds 30MB";
    if (VID.includes(ext) && file.size > MAX_VID) return "Video exceeds 800MB";
    return null;
  };

  const uploadOne = useCallback(async (item) => {
    setUploads((u) => u.map((x) => (x.uid === item.uid ? { ...x, status: "uploading", progress: 0, error: null } : x)));
    const fd = new FormData();
    fd.append("files", item.file);
    try {
      const res = await api.post(`/events/${event.id}/media`, fd, {
        onUploadProgress: (e) => {
          const p = Math.round((e.loaded * 100) / (e.total || 1));
          setUploads((u) => u.map((x) => (x.uid === item.uid ? { ...x, progress: p } : x)));
        },
      });
      const r0 = res.data?.results?.[0];
      if (r0 && r0.ok === false) {
        setUploads((u) => u.map((x) => (x.uid === item.uid ? { ...x, status: "error", error: r0.error || "Upload was rejected" } : x)));
        return;
      }
      setUploads((u) => u.map((x) => (x.uid === item.uid ? { ...x, status: "done", progress: 100 } : x)));
      loadAssets();
      reload();
    } catch (e) {
      setUploads((u) => u.map((x) => (x.uid === item.uid ? { ...x, status: "error", error: apiError(e) } : x)));
    }
  }, [event.id, loadAssets, reload]);

  const addFiles = (files) => {
    const arr = Array.from(files);
    const items = arr.map((file) => {
      const err = validate(file);
      return { uid: Math.random().toString(36).slice(2), file, name: file.name, size: file.size, progress: 0, status: err ? "error" : "pending", error: err };
    });
    setUploads((u) => [...items, ...u]);
    // upload with a small concurrency pool so 1000+ item folders don't open 1000 XHRs
    const queue = items.filter((i) => i.status === "pending");
    let idx = 0;
    const CONCURRENCY = 5;
    const worker = async () => {
      while (idx < queue.length) {
        const item = queue[idx++];
        await uploadOne(item);
      }
    };
    Array.from({ length: Math.min(CONCURRENCY, queue.length) }).forEach(worker);
  };

  const uploadZip = async (file) => {
    if (!file.name.toLowerCase().endsWith(".zip")) return toast.error("Please choose a .zip file");
    setZipBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post(`/events/${event.id}/media/zip`, fd, { timeout: 0 });
      toast.success(`Imported ${data.imported} of ${data.total} items from the ZIP`);
      loadAssets(); reload();
    } catch (e) { toast.error(apiError(e)); }
    finally { setZipBusy(false); }
  };

  const onDrop = (e) => { e.preventDefault(); setDrag(false); if (e.dataTransfer.files?.length) addFiles(e.dataTransfer.files); };

  const del = async (id) => {
    try { await api.delete(`/media/${id}`); loadAssets(); reload(); } catch (e) { toast.error(apiError(e)); }
  };

  const toggleFav = async (a) => {
    setAssets((its) => its.map((x) => x.id === a.id ? { ...x, favourite: !x.favourite } : x));
    try { await api.patch(`/media/${a.id}`, { favourite: !a.favourite }); loadAssets(); } catch (e) { toast.error(apiError(e)); }
  };

  const startAnalysis = async () => {
    setAnalysing(true);
    try {
      const { data } = await api.post(`/events/${event.id}/analyse`);
      setAnalysis(data);
      toast.success("Analysing gallery — flagging blurry, dark and duplicate photos");
    } catch (e) { toast.error(apiError(e)); }
    finally { setAnalysing(false); }
  };

  const bestOf = async (count) => {
    setBestBusy(true);
    try {
      const { data } = await api.post(`/events/${event.id}/best-of`, { count: parseInt(count) });
      toast.success(`Selected the best ${data.selected} of ${data.available} photos`);
      loadAssets(); reload();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBestBusy(false); }
  };

  const analysisRunning = analysis && ["queued", "running"].includes(analysis.status);
  const shown = assets.slice(0, visible);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-display text-3xl text-ivory">Add Photos & Videos</h2>
          <p className="text-muted-foreground text-sm mt-1">Upload single files, a whole folder, or a ZIP export from your booth. JPG, PNG, WebP, HEIC, MP4, MOV, WebM, M4V.</p>
        </div>
        <Button data-testid="upload-next-button" onClick={goNext} disabled={(stats?.total || 0) === 0} variant="outline" className="rounded-full border-gold text-gold hover:bg-gold/10">
          Arrange Highlights <ArrowRight className="w-4 h-4 ml-1.5" />
        </Button>
      </div>

      {/* Upload sources */}
      <div
        data-testid="upload-dropzone"
        onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
        onDragLeave={() => setDrag(false)}
        onDrop={onDrop}
        className={`rounded-2xl border-2 border-dashed transition-colors p-10 text-center ${drag ? "border-gold bg-gold/5" : "border-navy-border bg-navy-card"}`}>
        <input ref={inputRef} data-testid="upload-file-input" type="file" multiple accept={[...IMG, ...VID].map((e) => "." + e).join(",")} className="hidden"
          onChange={(e) => { if (e.target.files?.length) addFiles(e.target.files); e.target.value = ""; }} />
        <input ref={folderRef} data-testid="upload-folder-input" type="file" multiple webkitdirectory="" directory="" className="hidden"
          onChange={(e) => { if (e.target.files?.length) addFiles(e.target.files); e.target.value = ""; }} />
        <input ref={zipRef} data-testid="upload-zip-input" type="file" accept=".zip" className="hidden"
          onChange={(e) => { if (e.target.files?.[0]) uploadZip(e.target.files[0]); e.target.value = ""; }} />
        <UploadCloud className="w-12 h-12 text-gold mx-auto mb-3" />
        <p className="text-ivory text-lg font-medium">Drop files here, or choose a source</p>
        <p className="text-muted-foreground text-sm mt-1 mb-4">Handles large galleries with 1000+ items.</p>
        <div className="flex flex-wrap items-center justify-center gap-2">
          <Button data-testid="browse-files-button" onClick={() => inputRef.current?.click()} className="rounded-full bg-gold hover:bg-gold-dark text-navy font-semibold">
            <UploadCloud className="w-4 h-4 mr-1.5" /> Browse files
          </Button>
          <Button data-testid="browse-folder-button" variant="outline" onClick={() => folderRef.current?.click()} className="rounded-full border-navy-border text-champagne">
            <FolderUp className="w-4 h-4 mr-1.5" /> Upload a folder
          </Button>
          <Button data-testid="upload-zip-button" variant="outline" onClick={() => zipRef.current?.click()} disabled={zipBusy} className="rounded-full border-navy-border text-champagne">
            {zipBusy ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <FileArchive className="w-4 h-4 mr-1.5" />} Upload a ZIP
          </Button>
        </div>
      </div>

      {uploads.length > 0 && (
        <div className="space-y-2 max-h-72 overflow-y-auto">
          {uploads.slice(0, 200).map((u) => (
            <div key={u.uid} data-testid={`upload-row-${u.uid}`} className="bg-navy-card border border-navy-border rounded-xl px-4 py-3 flex items-center gap-4">
              {u.status === "done" ? <CheckCircle2 className="w-5 h-5 text-emerald-400 shrink-0" /> :
               u.status === "error" ? <AlertCircle className="w-5 h-5 text-red-400 shrink-0" /> :
               <Loader2 className="w-5 h-5 text-gold animate-spin shrink-0" />}
              <div className="flex-1 min-w-0">
                <div className="flex justify-between text-sm">
                  <span className="text-ivory truncate">{u.name}</span>
                  <span className="text-muted-foreground ml-2">{formatBytes(u.size)}</span>
                </div>
                {u.status === "error" ? <p className="text-xs text-red-400 mt-0.5">{u.error}</p> :
                 u.status !== "done" && <Progress value={u.progress} className="h-1.5 mt-1.5" />}
              </div>
              {u.status === "error" && (
                <Button data-testid={`retry-upload-${u.uid}`} size="sm" variant="ghost" onClick={() => uploadOne(u)} className="text-gold shrink-0">
                  <RotateCw className="w-4 h-4 mr-1" /> Retry
                </Button>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Smart gallery tools */}
      <div className="bg-navy-card border border-navy-border rounded-2xl p-4 space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h3 className="text-champagne text-sm uppercase tracking-wider flex items-center gap-2"><Sparkles className="w-4 h-4 text-gold" /> Smart gallery tools</h3>
          <div className="flex flex-wrap items-center gap-2">
            <Button data-testid="analyse-gallery-button" size="sm" onClick={startAnalysis} disabled={analysing || analysisRunning || (stats?.images || 0) === 0}
              className="rounded-full bg-gold hover:bg-gold-dark text-navy font-semibold">
              {analysing || analysisRunning ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <ScanSearch className="w-4 h-4 mr-1.5" />}
              {analysisRunning ? `Analysing ${analysis.done}/${analysis.total}` : "Analyse gallery"}
            </Button>
            <Select onValueChange={bestOf} disabled={bestBusy}>
              <SelectTrigger data-testid="best-of-select" className="w-44 h-9 bg-navy-deep border-navy-border">
                {bestBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <SelectValue placeholder="Auto best-of…" />}
              </SelectTrigger>
              <SelectContent>
                {[10, 20, 30, 50].map((n) => <SelectItem key={n} value={String(n)} data-testid={`best-of-${n}`}>Best {n} photos</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>

        {stats && (
          <div className="flex flex-wrap gap-4 text-xs text-muted-foreground">
            <span><span className="text-ivory font-medium">{stats.total}</span> items</span>
            <span><span className="text-ivory">{stats.images}</span> photos · <span className="text-ivory">{stats.videos}</span> videos</span>
            <span className="text-emerald-300">{stats.selected} selected</span>
            <span className="text-gold">{stats.favourites} favourites</span>
            {stats.analysed > 0 && <span className="text-orange-300">{stats.not_recommended} flagged not-recommended</span>}
          </div>
        )}

        {analysisRunning && <Progress value={analysis.total ? Math.round((analysis.done / analysis.total) * 100) : 5} className="h-1.5" />}

        {/* Filter tabs */}
        <div className="flex flex-wrap gap-2">
          {FILTERS.map(([v, l]) => {
            const c = v === "recommended" ? stats?.total - (stats?.not_recommended || 0)
              : v === "not_recommended" ? stats?.not_recommended
              : v === "favourites" ? stats?.favourites
              : stats?.total;
            return (
              <button key={v} data-testid={`filter-${v}`} onClick={() => setFilter(v)}
                className={`px-3.5 py-1.5 rounded-full text-xs border transition-colors ${filter === v ? "bg-gold text-navy border-gold" : "border-navy-border text-champagne hover:border-gold"}`}>
                {l}{stats ? ` (${c ?? 0})` : ""}
              </button>
            );
          })}
        </div>
      </div>

      {/* Gallery grid */}
      {assets.length === 0 ? (
        <div className="text-center py-16 text-muted-foreground border border-dashed border-navy-border rounded-2xl">
          {filter === "all" ? "No media yet — upload photos or videos above." : "Nothing in this view."}
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
            {shown.map((a) => (
              <div key={a.id} data-testid={`asset-${a.id}`}
                className={`group relative rounded-xl overflow-hidden border aspect-square bg-navy-card ${a.recommended === false ? "border-orange-500/50" : "border-navy-border"}`}>
                {a.thumb_path ? (
                  <img src={fileUrl(`/media/${a.id}/thumb`)} alt={a.original_filename} loading="lazy" className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    {a.kind === "video" ? <Film className="w-8 h-8 text-navy-border" /> : <ImageIcon className="w-8 h-8 text-navy-border" />}
                  </div>
                )}
                <QualityBadges a={a} />
                {a.kind === "video" && (
                  <div className="absolute bottom-1.5 left-1.5 bg-navy/80 rounded-full px-2 py-0.5 text-[10px] text-ivory flex items-center gap-1">
                    <Play className="w-2.5 h-2.5" /> {formatDuration(a.duration)}
                  </div>
                )}
                <button data-testid={`fav-asset-${a.id}`} onClick={() => toggleFav(a)}
                  className={`absolute top-1.5 right-8 rounded-full p-1.5 transition-opacity ${a.favourite ? "text-gold opacity-100" : "text-white/80 opacity-0 group-hover:opacity-100"}`}>
                  <Star className="w-3.5 h-3.5" fill={a.favourite ? "currentColor" : "none"} />
                </button>
                <button data-testid={`delete-asset-${a.id}`} onClick={() => del(a.id)}
                  className="absolute top-1.5 right-1.5 bg-red-600/90 rounded-full p-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
                  <Trash2 className="w-3.5 h-3.5 text-white" />
                </button>
              </div>
            ))}
          </div>
          {visible < assets.length && (
            <div className="flex justify-center">
              <Button data-testid="load-more-button" variant="outline" onClick={() => setVisible((v) => v + PAGE)} className="rounded-full border-navy-border text-champagne">
                <Wand2 className="w-4 h-4 mr-1.5" /> Load more ({assets.length - visible} remaining)
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
